import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, documentId } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { addBusinessDays, fromIsoDate, toIsoDate } from '../../utils/dateHelpers';
import { useFechaHoy } from '../../hooks/useFechaHoy';

// Formatea una key 'YYYY-MM-DD' como DD/MM/AAAA.
const formatKey = (key) => {
  if (!key || typeof key !== 'string') return '—';
  const [y, m, d] = key.split('-');
  return y && m && d ? `${d}/${m}/${y}` : key;
};

/**
 * Banner de salud del cierre diario. La sub-pestaña Vencimientos depende de que
 * el cierre nocturno corra con éxito (archiva las tomadoras a `vencimientos`).
 * Ese cierre se aborta si falla el export a Drive, sin señal visible en la app;
 * este banner lo hace evidente leyendo `dailyClosures` (y el error de `exportLogs`).
 *
 * No renderiza nada mientras el último cierre exitoso sea reciente (estado sano).
 */
const CierreHealthBanner = () => {
  const [estado, setEstado] = useState({ loading: true });
  // El cierre corre de madrugada: al cambiar el día hay que volver a mirarlo.
  const fechaHoy = useFechaHoy();

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'dailyClosures'),
          orderBy(documentId(), 'desc'),
          limit(7)
        ));
        const ultimoCompletado = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .find(c => c.status === 'completed');

        // Sano si el último cierre exitoso es del último día hábil o posterior.
        const umbral = toIsoDate(addBusinessDays(fromIsoDate(fechaHoy), -1));
        const sano = ultimoCompletado && ultimoCompletado.id >= umbral;

        if (sano) {
          if (!cancelled) setEstado({ loading: false, sano: true });
          return;
        }

        // Intentar traer el error del último export para dar contexto.
        let exportError = '';
        try {
          const logSnap = await getDoc(doc(db, 'exportLogs', `auto-${fechaHoy}`));
          if (logSnap.exists() && logSnap.data().status === 'error') {
            exportError = logSnap.data().error || '';
          }
        } catch (e) {
          // El log del día puede no existir; no es crítico.
        }

        if (!cancelled) {
          setEstado({
            loading: false,
            sano: false,
            ultimoCierre: ultimoCompletado ? ultimoCompletado.id : null,
            exportError,
          });
        }
      } catch (error) {
        console.error('CierreHealthBanner: no se pudo leer el estado del cierre:', error);
        if (!cancelled) setEstado({ loading: false, sano: true }); // no molestar si falla la lectura
      }
    };

    check();
    return () => { cancelled = true; };
  }, [fechaHoy]);

  if (estado.loading || estado.sano) return null;

  return (
    <div style={{
      backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
      padding: '0.75rem 1rem', borderRadius: '0.375rem', marginBottom: '1.5rem',
    }}>
      <span style={{ fontWeight: 600 }}>
        {estado.ultimoCierre
          ? `Último cierre exitoso: ${formatKey(estado.ultimoCierre)}. `
          : 'Todavía no se registró ningún cierre diario exitoso. '}
        El cierre automático puede estar fallando — revisá el export a Drive. Los vencimientos operados en vivo se muestran igual abajo.
      </span>
      {estado.exportError && (
        <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', fontWeight: 400 }}>
          Detalle del export: {estado.exportError}
        </div>
      )}
    </div>
  );
};

export default CierreHealthBanner;
